/**
 * ActionEditorTimeline — 动作编辑器时间轴（H5/PIXI 版）
 * 移植自 AS3 ActionEditorTimeline/TimelineWindow.as + TimelineStrip.as
 * 布局对齐 AS3：顶部帧号标尺、左侧深色控制块（播放+折叠）、主时间轴行、
 * 部件子时间轴（可纵向滚动）、底部深色控制条（上一帧/下一帧+横向滚动条）、
 * 右侧红色保存按钮与纵向滚动条。默认折叠只显示主时间轴。
 *
 * 接收（sessionId=7）：
 *   7_10 MTimeLineInfo  时间轴信息（createType=CREATE 重建 / UPDATA 增量）
 *   7_11                播放完成
 *   7_12 MInt32         当前帧变化
 *   7_15 MSelectPartInfo 选中部件变化（高亮对应子时间轴）
 *   1_13/1_14           锁定/解锁
 *   1_8  MTimelineToolbarBack 顶部编辑条点击回传（CORE -> 本模块）
 * 发送（->SCENE，引擎侧 ActionEditorMessageInterface 接收）：
 *   7_1 播放  7_2 停止  7_3 请求时间轴  7_4 跳帧(MInt32)
 *   7_5 帧编辑(MFrameEditInfo)  7_6 封面帧(MInt32)  7_7 保存
 *   7_9 选中部件帧区间(MTimelineFrameIndexesInfo)  7_42 衔接(MInt32)
 *   （退出动作编辑由 App.vue 顶栏返回键发 60_61，本模块不再提供退出按钮）
 * 发送（->CORE，App.vue .timeline_toolbar 浮层）：
 *   1_6 MTimelineToolbar 显示编辑条  1_7 隐藏编辑条
 */
import PGlobal from './PGlobal';
import M from './M';

// 帧类型（与 donging.script.action.KeyFrameType 一致）
const FRAME_EMPTY = 'empty';
const FRAME_NORMAL = 'normal';
const FRAME_TWEEN = 'tween';
const FRAME_KEY = 'key';
const PRIORITY_TO_TYPE = [FRAME_EMPTY, FRAME_NORMAL, FRAME_TWEEN, FRAME_KEY];
const TYPE_PRIORITY = { empty: 0, normal: 1, tween: 2, key: 3 };

// 帧颜色（对齐 TimelineStrip.as）
// 帧色对齐AS3 ui图集纹理烘焙色：emptyFrame=0xfafafa normalFrame=0xd8d8d8
// tweenFrame=0xadadad keyFrame=0x8a8a8a selectedFrame=0x1d3456，24帧分隔线line=0x545454
const COLOR_EMPTY = 0xfafafa;
const COLOR_NORMAL = 0xd8d8d8;
const COLOR_TWEEN = 0xadadad;
const COLOR_KEY = 0x8a8a8a;
const COLOR_SELECTED = 0x1d3456;
// AS3实测帧间分隔线0x3d3d3d（每帧左缘），24帧线同色加粗
const COLOR_FRAME_LINE = 0x3d3d3d;
const COLOR_LINE24 = 0x3d3d3d;
const COLOR_CURSOR = 0xd81e06;
const COLOR_PREVIEW = 0x27ae60;
// 界面配色（对齐 AS3 深色控制区 + 浅色帧区）
const COLOR_DARK = 0x3a3a3a;
const COLOR_DARK2 = 0x2f2f2f;
const COLOR_RULER_BKG = 0xf2f2f2;
const COLOR_ROW_BKG = 0xe8e8e8;
const COLOR_ROW_BKG_SEL = 0xc2c2c2;
const COLOR_ROW_BORDER_SEL = 0xff0000;

// 时间轴网格总帧数（对齐 AS3 TimelineWindow: mMaxTime(10s) * ActionScript.FRAMERATE(24) = 240）
// tolFrameNum只管内容帧点位置与帧选中上限，网格/滚动宽度固定240
const MAX_FRAME_NUM = 240;

// 帧编辑操作（对齐 ActionFrameEditType.as）
const EDIT_KEY = '1';
const EDIT_INSERT = '2';
const EDIT_DELETE = '3';
const EDIT_ERASE = '4';
const EDIT_COPY = '5';
const EDIT_PASTE = '6';

// 编辑条按钮（对齐 TimelineWindow CP_*）
const CP = {
    key: { id: 'key', label: '关键帧', edit: EDIT_KEY },
    insert: { id: 'insert', label: '插入', edit: EDIT_INSERT },
    del: { id: 'delete', label: '删除', edit: EDIT_DELETE },
    erase: { id: 'erase', label: '清除', edit: EDIT_ERASE },
    copy: { id: 'copy', label: '复制', edit: EDIT_COPY },
    paste: { id: 'paste', label: '粘贴', edit: EDIT_PASTE },
    preview: { id: 'preview', label: '封面' },
    copyFromAction: { id: 'copyFromAction', label: '衔接' }
};

const MAIN_TIMELINE_ID = 'MAIN_TIMELIEN_ID'; // ActionScript.MAIN_TIMELIEN_ID
const CREATE = 'CREATE';

// 布局（对齐 AS3 TimelineWindow：左列86 标尺16 主时间轴52 子帧32 底部24 右侧52）
const RULER_H = 16;        // 帧号标尺
const LEFT_W = 66;         // 左侧控制/部件名列宽
const RIGHT_W = 52;        // 右侧保存按钮/纵向滚动条列宽（AS3 mRightContianerW）
const MAIN_H = 52;         // 主时间轴行高（AS3 mMainFrameHeight）
const SUB_H = 30;          // 子时间轴行高
const SUB_GAP = 1;
const BOTTOM_H = 24;       // 底部控制条
const VSCROLL_W = 12;      // 子时间轴纵向滚动条宽
const FRAME_W = 9;         // 帧宽
const MIN_H = 96;
const MAX_H = 252;

export default class ActionEditorTimeline {

    constructor(container) {
        this.parent = container;
        this.frameWidth = FRAME_W;
        this.initData = false;
        this.isPlaying = false;
        this.locked = false;
        this.subVisible = false;

        this.currentFrameIndex = 0;
        this.previewFrameIndex = 0;
        this.tolFrameNum = 1;
        this.isEdit = false;
        this.canPastePartId = '';
        this.canCopyFromOtherAction = false;
        this.bundleId = '';

        this.mainStrip = this._createStrip(MAIN_TIMELINE_ID, 'PART', '主体');
        this.subStrips = [];
        this.currentStrip = this.mainStrip;
        this.scrollX = 0;
        this.scrollY = 0;
        // 交互控件脏标记：播放中每帧redraw时保持_uiContainer不重建，
        // 否则按钮刚销毁重建、worldTransform未更新，pointerdown命中测试间歇性打空
        this._uiDirty = true;
        this._uiContainer = null;
    }

    _createStrip(partId, type, label, png) {
        return {
            partId: partId,
            type: type,
            label: label || '',
            png: png || null,
            pngSprite: null,
            frames: [],
            totalFrame: 0,
            selectMin: -1,
            selectMax: -1
        };
    }

    start(platform, moduleId, moduleName) {
        PGlobal.platform = platform;
        this.moduleId = moduleId;
        this.moduleName = moduleName;
        this.m = new M(moduleId, moduleName);

        this.m.listen(7, 10, proto.com.proto.actioneditortimeline.MTimeLineInfo, (d) => this.onTimelineInfo(d.data));
        this.m.listen(7, 11, null, () => this.onPlayComplete());
        this.m.listen(7, 12, proto.com.proto.s.MInt32, (d) => this.onChangeFrame(d.data));
        this.m.listen(7, 15, proto.com.proto.actioneditortimeline.MSelectPartInfo, (d) => this.onSelectPartInfo(d.data));
        this.m.listen(1, 13, null, () => this.setLock(true));
        this.m.listen(1, 14, null, () => this.setLock(false));
        // CORE 顶部浮出编辑条点击回传（对齐 AS3 RM1_8 -> receiveEditFrame）
        this.m.listen(1, 8, proto.com.proto.s.MTimelineToolbarBack, (d) => this.onEditBarBack(d.data));

        this.app = new PIXI.Application(100, 100, { backgroundColor: COLOR_DARK, antialias: true });
        //context丢失后ticker仍每帧渲染到死context上（INVALID_OPERATION刷屏）：lost停帧。
        //不在restored时自动恢复：PIXI4重建GL不完整，恢复后渲染仍报INVALID刷屏，只能停帧等刷新
        this.app.view.addEventListener('webglcontextlost', () => {
            if (this.app && this.app.ticker) this.app.ticker.stop();
        });
        this.parent.appendChild(this.app.view);
        this.app.view.style.display = 'block';
        this.stage = this.app.stage;
        this.stage.interactive = true;

        this.gBkg = new PIXI.Graphics();
        this.gStrips = new PIXI.Graphics();
        this.gCursor = new PIXI.Graphics();
        this.gUi = new PIXI.Graphics();
        this.texts = new PIXI.Container();
        this.stripMask = new PIXI.Graphics();
        this.gStrips.mask = this.stripMask;
        this.iconsMask = new PIXI.Graphics();

        // 交互热区（标尺+主时间轴共用主热区，子时间轴单独热区）
        this.mainHit = new PIXI.Graphics();
        this.mainHit.interactive = true;
        this.subHit = new PIXI.Graphics();
        this.subHit.interactive = true;
        this.thumbHit = new PIXI.Graphics();   // 底部横向滚动条
        this.thumbHit.interactive = true;

        this.stage.addChild(this.gBkg, this.gStrips, this.texts, this.gCursor,
            this.mainHit, this.subHit, this.thumbHit, this.gUi);

        this.controlPanelShown = false;  // 顶部浮出编辑条（.timeline_toolbar）是否显示中

        this.icons = new PIXI.Container();     // 部件缩略图（纵向裁切，不裁左列）
        this.icons.mask = this.iconsMask;
        this.stage.addChild(this.icons);

        this._bindHit();

        this.app.view.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
        this._resizeObserver = new ResizeObserver(() => this.resize());
        this._resizeObserver.observe(this.parent);
        this._onKeyDown = (e) => this.onKeyDown(e);
        window.addEventListener('keydown', this._onKeyDown);
        this.resize();

        // 请求时间轴数据（AS3 onDisplay -> sendRequestTimelineInfo）
        this.send(7, 3, null);
    }

    resize() {
        const w = this.parent.clientWidth;
        const h = this.parent.clientHeight;
        if (!w || !h) return;
        this.app.renderer.resize(w, h);
        this._uiDirty = true;
        this.redraw();
    }

    // 从DOM取实时宽高（容器刚显示时 renderer 可能还是旧尺寸）
    _size() {
        const w = this.parent.clientWidth;
        const h = this.parent.clientHeight;
        if (w && h && (this.app.renderer.width !== w || this.app.renderer.height !== h)) {
            this.app.renderer.resize(w, h);
        }
        return { W: this.app.renderer.width / this.app.renderer.resolution, H: this.app.renderer.height / this.app.renderer.resolution };
    }

    // ================= 入站消息 =================

    onTimelineInfo(info) {
        if (!info) return;
        this.canPastePartId = info.canpastepartid || '';
        this.isEdit = !!info.isedit;
        this.previewFrameIndex = info.previewframeindex || 0;
        this.bundleId = info.bundleid || '';
        this.canCopyFromOtherAction = !!info.cancopyfromotheraction;

        const subs = info.subtimelinesList || [];
        this.tolFrameNum = 1;

        if (info.createtype === CREATE) {
            if (subs.length) this._fillStrip(this.mainStrip, subs[0]);
            const prevPartId = this.currentStrip ? this.currentStrip.partId : null;
            this.subStrips = [];
            for (let i = 1; i < subs.length; i++) {
                const s = subs[i];
                const strip = this._createStrip(s.partid, s.type, s.partlabel);
                this._fillStrip(strip, s);
                this.subStrips.push(strip);
            }
            // 重建后currentStrip按partId重指到新对象，无匹配回退主时间轴（对齐AS3 mCurrentSelectedTimeline）
            const kept = prevPartId && this.subStrips.find(t => t.partId === prevPartId);
            this.currentStrip = kept || this.mainStrip;
            if (!this.initData) {
                this.scrollX = 0;
                this.scrollY = 0;
                // 默认折叠只显示主时间轴（对齐 AS3 初始化调用 hideSubTimeline -> MIN_HEIGHT）
                this.subVisible = false;
            } else {
                // 后续CREATE（如主时间轴加关键帧）不改动展开状态，只收敛滚动位置
                this.scrollY = Math.max(0, Math.min(this.maxScrollY(), this.scrollY));
            }
            this._applyContainerHeight();
        } else {
            if (subs.length) this._fillStrip(this.mainStrip, subs[0]);
            for (let i = 1; i < subs.length; i++) {
                const s = subs[i];
                const frames = this._decodeFrames(s);
                let strip = this.subStrips.find(t => t.partId === s.partid);
                if (strip) {
                    if (s.type === 'HOLDBUNDLE' && frames.length === 0) {
                        if (this.currentStrip === strip) this.currentStrip = this.mainStrip;
                        this.subStrips.splice(this.subStrips.indexOf(strip), 1);
                    } else {
                        strip.label = s.partlabel;
                        this._fillStrip(strip, s);
                    }
                } else if (!(s.type === 'HOLDBUNDLE' && frames.length === 0)) {
                    strip = this._createStrip(s.partid, s.type, s.partlabel);
                    this._fillStrip(strip, s);
                    let insertAt = this.subStrips.length;
                    for (let j = i - 1; j >= 1; j--) {
                        const idx = this.subStrips.findIndex(t => t.partId === subs[j].partid);
                        if (idx >= 0) { insertAt = idx + 1; break; }
                    }
                    this.subStrips.splice(insertAt, 0, strip);
                }
            }
        }

        if (!this.initData) this.initData = true;
        this._uiDirty = true;
        const sel = info.selectedframeindexesList || [];
        if (sel.length) this.selectRange(Math.min.apply(null, sel), Math.max.apply(null, sel), false);
        if (typeof info.currentframeindex === 'number') this.changeFrame(info.currentframeindex, false);
        this.redraw();
    }

    _fillStrip(strip, m) {
        strip.frames = this._decodeFrames(m);
        strip.totalFrame = m.totalframe || strip.frames.length;
        if (this.tolFrameNum < m.totalframe) this.tolFrameNum = m.totalframe;
        if (m.partpng && m.partpng.length) {
            strip.png = 'data:image/png;base64,' + m.partpng;
        }
    }

    // 解码 2bit*4 压缩帧数据（对齐 AS3 getTimelineFramesByMSubTimeline）
    // 注意：proto toObject() 后 bytes 字段是 base64 字符串
    _decodeFrames(m) {
        const total = m.totalframe || 0;
        let bytes = m.timelineframes;
        const frames = [];
        if (!bytes || !total) return frames;
        if (typeof bytes === 'string') {
            const bin = atob(bytes);
            const arr = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
            bytes = arr;
        }
        const n = Math.ceil(total / 4);
        for (let i = 0; i < n; i++) {
            const b = bytes[i] & 0xff;
            frames.push(PRIORITY_TO_TYPE[(b & 192) >> 6]);
            frames.push(PRIORITY_TO_TYPE[(b & 48) >> 4]);
            frames.push(PRIORITY_TO_TYPE[(b & 12) >> 2]);
            frames.push(PRIORITY_TO_TYPE[b & 3]);
        }
        frames.length = total;
        return frames;
    }

    onPlayComplete() {
        this.isPlaying = false;
        this._uiDirty = true;
        this.redraw();
    }

    onChangeFrame(data) {
        if (!this.initData || !data) return;
        this.changeFrame(data.value, false);
    }

    // 7_15 部件面板选中部件 -> 高亮并滚动到对应子时间轴
    // 面板收起时也记住选中行（不自动展开），下次展开即为选中状态并滚到该行
    onSelectPartInfo(data) {
        const partId = data && data.mpartinfo ? data.mpartinfo.id : null;
        if (!partId) return;
        const strip = this.subStrips.find(t => t.partId === partId);
        if (!strip) return;
        this.clearSelection();
        this.currentStrip = strip;
        this.selectRange(this.currentFrameIndex, this.currentFrameIndex, false);
        //对齐AS3 showSubTimelineByPartId：同步保证当前帧横向可见
        this.changeFrame(this.currentFrameIndex, false);
        if (this.subVisible) this._revealStrip(strip);
        this.redraw();
    }

    // 纵向滚动使子时间轴行可见（顶部对齐）
    _revealStrip(strip) {
        const idx = this.subStrips.indexOf(strip);
        if (idx < 0) return;
        this.scrollY = Math.max(0, Math.min(this.maxScrollY(), idx * (SUB_H + SUB_GAP)));
    }

    setLock(flag) {
        this.locked = flag;
        this.hideEditBar();
        this._uiDirty = true;
        this.redraw();
    }

    // ================= 出站消息 =================

    send(sId, mId, protoMsg) {
        this.m.send(SCENE, sId, mId, protoMsg);
    }

    sendGotoAndStop(index) {
        const m = new proto.com.proto.s.MInt32();
        m.setValue(index);
        this.send(7, 4, m);
    }

    // 按住时间轴帧期间部件红边保持显示，弹起后移除
    sendHoldHighlight(hold) {
        const m = new proto.com.proto.s.MBool();
        m.setValue(!!hold);
        this.send(7, 43, m);
    }

    sendEditFrame(editType) {
        if (!this.currentStrip) return;
        const m = new proto.com.proto.actioneditortimeline.MFrameEditInfo();
        m.setOperatetype(editType);
        m.setOperatepartid(this.currentStrip.partId);
        m.setFrameindex(this.currentFrameIndex);
        this.send(7, 5, m);
        if (editType === EDIT_COPY) {
            this.canPastePartId = this.currentStrip.partId;
        }
    }

    // 对齐 AS3 selectFrames/sendSelectPart：单帧选择发送部件id，区间选择发送空
    sendSelectPartFrames() {
        if (!this.currentStrip) return;
        const m = new proto.com.proto.actioneditortimeline.MTimelineFrameIndexesInfo();
        const s = this.currentStrip;
        const indexes = [];
        if (s.selectMin >= 0) {
            for (let i = s.selectMin; i <= s.selectMax; i++) indexes.push(i);
        }
        if (indexes.length === 0) indexes.push(this.currentFrameIndex);
        const isRange = s.selectMin >= 0 && s.selectMax > s.selectMin;
        m.setOperatepartid(isRange ? '' : s.partId);
        m.setFrameindexesList(indexes);
        this.send(7, 9, m);
    }

    sendSetPreviewFrame(index) {
        const m = new proto.com.proto.s.MInt32();
        m.setValue(index);
        this.send(7, 6, m);
    }

    sendCopyFromAction(index) {
        const m = new proto.com.proto.s.MInt32();
        m.setValue(index);
        this.send(7, 42, m);
    }

    sendSave() { this.send(7, 7, null); }

    // ================= 几何 =================

    _mainY() { return RULER_H; }
    _subTop() { return RULER_H + MAIN_H; }
    _subBottom(H) { return this.subVisible ? H - BOTTOM_H : this._subTop(); }
    _visW(W) { return W - LEFT_W - RIGHT_W; }
    _gridFrameNum() { return Math.max(this.tolFrameNum, MAX_FRAME_NUM); }
    _contentW() { return this._gridFrameNum() * this.frameWidth; }
    _frameX(i) { return LEFT_W + i * this.frameWidth - this.scrollX; }

    maxScrollX() {
        const W = this.app.renderer.width / this.app.renderer.resolution;
        return Math.max(0, this._contentW() - this._visW(W));
    }
    maxScrollY() {
        const H = this.app.renderer.height / this.app.renderer.resolution;
        const subH = this.subStrips.length * (SUB_H + SUB_GAP);
        return Math.max(0, subH - (this._subBottom(H) - this._subTop()));
    }

    _frameIndexAtX(stageX) {
        let idx = Math.floor((stageX - LEFT_W + this.scrollX) / this.frameWidth);
        if (idx < 0) return 0;
        const maxIdx = this._gridFrameNum() - 1;
        return idx > maxIdx ? maxIdx : idx;
    }

    _stripAtSubY(stageY) {
        const rel = stageY - this._subTop() + this.scrollY;
        if (rel < 0) return null;
        return this.subStrips[Math.floor(rel / (SUB_H + SUB_GAP))] || null;
    }

    // ================= 交互 =================

    _bindHit() {
        this.mainHit.on('pointerdown', (e) => this.onStripDown(e, this.mainStrip));
        this.subHit.on('pointerdown', (e) => {
            const strip = this._stripAtSubY(e.data.getLocalPosition(this.stage).y);
            if (strip) this.onStripDown(e, strip);
        });
        // 横向滚动条拖拽
        this.thumbHit.on('pointerdown', (e) => {
            if (this.locked) return;
            const W = this.app.renderer.width / this.app.renderer.resolution;
            const trackW = this._visW(W);
            const maxX = this.maxScrollX();
            if (maxX <= 0) return;
            const ratio = this._visW(W) / this._contentW();
            const thumbW = Math.max(24, trackW * ratio);
            const startX = e.data.getLocalPosition(this.stage).x;
            const startScroll = this.scrollX;
            const thumbX = LEFT_W + (startScroll / maxX) * (trackW - thumbW);
            const inThumb = startX >= thumbX && startX <= thumbX + thumbW;
            const move = (ev) => {
                const x = ev.data.getLocalPosition(this.stage).x;
                if (inThumb) {
                    this.scrollX = Math.max(0, Math.min(maxX,
                        startScroll + (x - startX) / (trackW - thumbW) * maxX));
                } else {
                    // 点击轨道：以点击处为中心
                    const target = Math.max(0, Math.min(trackW - thumbW, x - LEFT_W - thumbW / 2));
                    this.scrollX = target / (trackW - thumbW) * maxX;
                }
                this.hideEditBar();
                this.redraw();
            };
            const up = () => {
                this.stage.off('pointermove', move);
                this.stage.off('pointerup', up);
                this.stage.off('pointerupoutside', up);
            };
            this.stage.on('pointermove', move);
            this.stage.on('pointerup', up);
            this.stage.on('pointerupoutside', up);
        });
        // 标尺区域滚轮/点击已在 mainHit 覆盖（mainHit 包含标尺行）
    }

    onStripDown(e, strip) {
        if (this.locked || !this.initData) return;
        const anchorIndex = this._frameIndexAtX(e.data.getLocalPosition(this.stage).x);
        // 对齐AS3：末帧之后的空白格也可点选（选中后通过编辑条"设为关键帧"扩展动作长度）
        if (this.isPlaying) this.stop();
        this.hideEditBar();
        this.clearSelection();
        this.currentStrip = strip;

        this.sendHoldHighlight(true);
        this.selectRange(anchorIndex, anchorIndex, true);
        this.changeFrame(anchorIndex, true);

        const move = (ev) => {
            const idx = this._frameIndexAtX(ev.data.getLocalPosition(this.stage).x);
            this.selectRange(Math.min(anchorIndex, idx), Math.max(anchorIndex, idx), true);
            this.changeFrame(idx, true);
        };
        const up = () => {
            this.stage.off('pointermove', move);
            this.stage.off('pointerup', up);
            this.stage.off('pointerupoutside', up);
            this.sendHoldHighlight(false);
            if (!this.isPlaying) this.showEditBar();
        };
        this.stage.on('pointermove', move);
        this.stage.on('pointerup', up);
        this.stage.on('pointerupoutside', up);
    }

    // 选中帧区间；isSendMsg=true 时上报 7_9
    // 对齐 AS3 selectFrames：选主时间轴时所有子时间轴同步选中；选子时间轴时取消其它轴选中
    selectRange(min, max, isSendMsg) {
        const s = this.currentStrip;
        if (!s) return;
        s.selectMin = min;
        s.selectMax = max;
        if (s === this.mainStrip) {
            this.subStrips.forEach(t => { t.selectMin = min; t.selectMax = max; });
        } else {
            this.mainStrip.selectMin = this.mainStrip.selectMax = -1;
            this.subStrips.forEach(t => {
                if (t !== s) { t.selectMin = t.selectMax = -1; }
            });
        }
        if (isSendMsg) this.sendSelectPartFrames();
        this.redraw();
    }

    clearSelection() {
        this.mainStrip.selectMin = this.mainStrip.selectMax = -1;
        this.subStrips.forEach(t => { t.selectMin = t.selectMax = -1; });
    }

    changeFrame(index, isSendMsg) {
        if (index < 0) index = 0;
        this.currentFrameIndex = index;
        this._ensureFrameVisible(index);
        if (isSendMsg) this.sendGotoAndStop(index);
        this.redraw();
    }

    _ensureFrameVisible(index) {
        const W = this.app.renderer.width / this.app.renderer.resolution;
        const visW = this._visW(W);
        const px = (index + 1) * this.frameWidth;
        if (px - this.scrollX > visW) this.scrollX = px - visW;
        if (index * this.frameWidth < this.scrollX) this.scrollX = index * this.frameWidth;
        this.scrollX = Math.max(0, Math.min(this.maxScrollX(), this.scrollX));
    }

    onWheel(e) {
        if (!this.initData || this.locked) return;
        e.preventDefault();
        const rect = this.app.view.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (this.subVisible && y > this._subTop() && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            this.scrollY = Math.max(0, Math.min(this.maxScrollY(), this.scrollY + e.deltaY * 0.5));
        } else {
            const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
            this.scrollX = Math.max(0, Math.min(this.maxScrollX(), this.scrollX + d * 0.5));
        }
        this.hideEditBar();
        this.redraw();
    }

    // ================= 播放控制 =================

    play() {
        if (!this.initData || this.locked) return;
        this.send(7, 1, null);
        this.isPlaying = true;
        this.hideEditBar();
        this._uiDirty = true;
        this.redraw();
    }

    stop() {
        this.send(7, 2, null);
        this.isPlaying = false;
        this._uiDirty = true;
        this.redraw();
    }

    // 键盘左/右方向键：上一帧/下一帧（对齐AS3时间轴键盘切帧）
    // 仅动作时间轴可见时响应（隐藏容器clientWidth=0，避免影响动画编辑态的方向键行为）
    onKeyDown(e) {
        if (e.keyCode !== 37 && e.keyCode !== 39) return;
        if (!this.parent || this.parent.clientWidth === 0) return;
        if (this.locked || !this.initData) return;
        const t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        if (e.keyCode === 37) this.prevFrame();
        else this.nextFrame();
    }

    prevFrame() {
        if (!this.initData) return;
        this.changeFrame(Math.max(0, this.currentFrameIndex - 1), true);
    }

    nextFrame() {
        if (!this.initData) return;
        this.changeFrame(Math.min(this._gridFrameNum() - 1, this.currentFrameIndex + 1), true);
    }

    // 通知外层容器调整高度（对齐 AS3 sendChangeHeight 7_8：收起96 / 展开252）
    // CORE 侧 App.vue 已接收 7_8 MFloat 调整 .scene-container/.action-timeline-container
    _applyContainerHeight() {
        const h = this.subVisible ? MAX_H : MIN_H;
        const m = new proto.com.proto.s.MFloat();
        m.setValue(h);
        this.m.send(CORE, 7, 8, m);
        // 兜底：若 CORE 处理未生效仍直接调整
        const container = this.parent.parentElement || this.parent;
        if (container && container.style) container.style.height = h + 'px';
        const scene = document.querySelector('.scene-container');
        if (scene) scene.style.bottom = h + 'px';
        this.resize();
    }

    toggleSub() {
        // 展开/收起改变容器高度，编辑条旧位置会悬空：先收，布局完成后按新几何重发跟随
        //（收起后子时间轴行不可见，仅主时间轴上的选区需要跟随重显）
        const reopen = this.controlPanelShown && this.currentStrip &&
            (!this.subVisible || this.currentStrip === this.mainStrip);
        this.subVisible = !this.subVisible;
        this.hideEditBar();
        this._uiDirty = true;
        this._applyContainerHeight();
        // 展开时若已选中子时间轴（如收起状态下点过部件），滚动到该行使选中可见
        if (this.subVisible && this.currentStrip && this.currentStrip !== this.mainStrip) {
            this._revealStrip(this.currentStrip);
            this.redraw();
        }
        if (reopen) {
            // 等 7_8 高度消息与容器样式生效后再发 1_6，否则CORE按旧offset算top
            setTimeout(() => this.showEditBar(), 60);
        }
    }

    // ================= 编辑条（对齐 AS3 sendControlPanelMsg -> SM1_6/SM1_7/RM1_8） =================
    // 编辑条是 CORE 侧 HTML 浮层（App.vue .timeline_toolbar，icon-key/insert/delete/preview/erase/copy/paste）
    // 本模块只发 1_6 MTimelineToolbar{offsetx,offsety,itemsList}/1_7 隐藏，点击经 1_8 MTimelineToolbarBack{id} 回传

    _editBarItems() {
        const s = this.currentStrip;
        if (!s || s.selectMin < 0) return [];
        const type = s.frames[this.currentFrameIndex] || FRAME_EMPTY;
        const selLen = s.selectMax - s.selectMin + 1;
        const frames = [];
        for (let i = s.selectMin; i <= s.selectMax; i++) frames.push(s.frames[i] || FRAME_EMPTY);
        const highest = frames.reduce((a, b) => TYPE_PRIORITY[b] > TYPE_PRIORITY[a] ? b : a, FRAME_EMPTY);

        const list = [CP.key];
        if (selLen <= 1) {
            if (type !== FRAME_EMPTY) {
                list.push(CP.insert, CP.del);
                if (s === this.mainStrip && this.currentFrameIndex !== this.previewFrameIndex) list.push(CP.preview);
                if (type === FRAME_KEY) list.push(CP.erase);
            }
        } else if (highest !== FRAME_EMPTY) {
            list.push(CP.del);
            if (highest === FRAME_KEY) list.push(CP.erase);
        }
        if (s.selectMin < s.frames.length) list.push(CP.copy);
        if (this.canPastePartId === s.partId) list.push(CP.paste);
        if (this.canCopyFromOtherAction && s === this.mainStrip && this.currentFrameIndex === 0) {
            list.push(CP.copyFromAction);
        }
        return list;
    }

    // 对齐 AS3 TimelineWindow.sendControlPanelMsg：offsetx=左列宽+帧中心-横向滚动，offsety=子时间轴行位置
    showEditBar() {
        const items = this._editBarItems();
        if (!items.length) { this.hideEditBar(); return; }
        const s = this.currentStrip;
        const dx = LEFT_W + (this.currentFrameIndex + 0.5) * this.frameWidth - this.scrollX;
        let dy = 0;
        if (s !== this.mainStrip) {
            const idx = this.subStrips.indexOf(s);
            dy = idx >= 0 ? this._subTop() + idx * (SUB_H + SUB_GAP) - this.scrollY : 0;
        }
        const bar = new proto.com.proto.s.MTimelineToolbar();
        bar.setOffsetx(Math.round(dx));  // int32 字段不能传小数
        bar.setOffsety(Math.round(dy));
        items.forEach((it) => {
            const ti = new proto.com.proto.s.MTimelineToolbarItem();
            ti.setId(it.id);
            bar.addItems(ti);
        });
        this.m.send(CORE, 1, 6, bar);
        this.controlPanelShown = true;
    }

    // 对齐 AS3 sendHideControlPanel -> SM1_7
    hideEditBar() {
        if (this.controlPanelShown) {
            this.controlPanelShown = false;
            this.m.send(CORE, 1, 7, null);
        }
    }

    // 对齐 AS3 receiveEditFrame -> editFrame(id)
    onEditBarBack(data) {
        if (!data || data.id == null) return;
        this.editFrame(String(data.id));
    }

    editFrame(id) {
        switch (id) {
            case 'key': this.sendEditFrame(EDIT_KEY); break;
            case 'insert': this.sendEditFrame(EDIT_INSERT); break;
            case 'delete': this.sendEditFrame(EDIT_DELETE); break;
            case 'erase': this.sendEditFrame(EDIT_ERASE); break;
            case 'copy':
                this.sendEditFrame(EDIT_COPY);
                this.showEditBar(); // 对齐AS3：复制后刷新出粘贴按钮
                break;
            case 'paste': this.sendEditFrame(EDIT_PASTE); break;
            case 'preview': this.sendSetPreviewFrame(this.currentFrameIndex); break;
            case 'copyFromAction': this.sendCopyFromAction(this.currentFrameIndex); break;
        }
    }

    // ================= 渲染 =================

    // 文字对象按 内容+样式 复用：PIXI4 每个 Text 自带 canvas 纹理且登记进全局 BaseTextureCache，
    // redraw 播放中每帧执行，若每帧 new Text 再 removeChildren（不 destroy），
    // 纹理只增不减，播放几次即耗尽显存（IOSurface 分配失败）→ GPU 进程重置、context lost 黑屏
    _getText(str, style) {
        const key = style.fontSize + '|' + style.fill + '|' + str;
        let list = this._textCache.get(key);
        if (!list) {
            list = [];
            this._textCache.set(key, list);
        }
        const used = this._textUse.get(key) || 0;
        this._textUse.set(key, used + 1);
        if (used < list.length) return list[used];
        const t = new PIXI.Text(str, style);
        list.push(t);
        return t;
    }

    _clearTextCache() {
        if (!this._textCache) return;
        this._textCache.forEach(list => list.forEach(t => {
            if (t.parent) t.parent.removeChild(t);
            t.destroy(true);
        }));
        this._textCache.clear();
    }

    redraw() {
        if (!this.app) return;
        const size = this._size();
        const W = size.W;
        const H = size.H;
        if (W <= 0 || H <= 0) return;
        if (!this._textCache) {
            this._textCache = new Map();
            this._textUse = new Map();
        }
        // 兜底上限：切换大量动作/部件后标签种类累积过多时整体回收
        if (this._textCache.size > 500) this._clearTextCache();
        this._textUse.clear();

        this.gBkg.clear();
        this.gStrips.clear();
        this.gCursor.clear();
        this.gUi.clear();
        this.stripMask.clear();
        this.iconsMask.clear();
        this.mainHit.clear();
        this.subHit.clear();
        this.thumbHit.clear();
        this.texts.removeChildren();
        this.icons.removeChildren();

        const visW = this._visW(W);
        const mainY = this._mainY();
        const subTop = this._subTop();
        const subBottom = this._subBottom(H);

        // ---- 整体底色 ----
        this.gBkg.beginFill(COLOR_DARK).drawRect(0, 0, W, H).endFill();

        // ---- 标尺（白底 + 每5帧刻度/数字） ----
        this.gBkg.beginFill(COLOR_RULER_BKG).drawRect(LEFT_W, 0, visW, RULER_H).endFill();
        const firstIdx = Math.max(0, Math.floor(this.scrollX / this.frameWidth));
        const lastIdx = Math.min(this._gridFrameNum(), Math.ceil((this.scrollX + visW) / this.frameWidth) + 1);
        for (let i = firstIdx; i <= lastIdx; i++) {
            if ((i + 1) % 5 !== 0) continue;
            const x = this._frameX(i);
            if (x < LEFT_W || x > W - RIGHT_W) continue;
            const t = this._getText(String(i + 1), { fontSize: 9, fill: 0x555555 });
            t.position.set(x + this.frameWidth / 2 - t.width / 2, 3);
            this.texts.addChild(t);
        }

        // ---- 帧格子裁切区（标尺以下、底条以上，左列到右列之间） ----
        this.stripMask.beginFill(0).drawRect(LEFT_W, mainY, visW, Math.max(0, subBottom - mainY)).endFill();
        // 部件缩略图裁切：纵向上限子时间轴区，横向不限左列
        this.iconsMask.beginFill(0).drawRect(0, subTop, W - RIGHT_W, Math.max(0, subBottom - subTop)).endFill();

        // ---- 主时间轴 ----
        this._drawStrip(this.mainStrip, mainY, MAIN_H, true, W);

        // ---- 子时间轴 ----
        if (this.subVisible) {
            this.subStrips.forEach((s, i) => {
                const y = subTop + i * (SUB_H + SUB_GAP) - this.scrollY;
                if (y + SUB_H < subTop || y > subBottom) return;
                this._drawStrip(s, y, SUB_H, false, W);
                // 行头：部件缩略图 + 名称（浅色底，选中0xc2c2c2底+红边框，对齐AS3 SubTimelineStrip）
                const isSel = this.currentStrip === s;
                this.gBkg.lineStyle(isSel ? 1 : 0, COLOR_ROW_BORDER_SEL)
                    .beginFill(isSel ? COLOR_ROW_BKG_SEL : COLOR_ROW_BKG)
                    .drawRect(0, y, LEFT_W, SUB_H).endFill();
                this.gBkg.lineStyle(0);
                let labelX = 4;
                if (s.png) {
                    if (!s.pngSprite) {
                        s.pngSprite = new PIXI.Sprite(PIXI.Texture.fromImage(s.png));
                    }
                    s.pngSprite.width = s.pngSprite.height = SUB_H - 6;
                    s.pngSprite.position.set(3, y + 3);
                    this.icons.addChild(s.pngSprite);
                    labelX = SUB_H - 2;
                }
                const label = this._getText(s.label || s.partId, { fontSize: 10, fill: 0x333333 });
                label.position.set(labelX, y + (SUB_H - 12) / 2);
                this.texts.addChild(label);
            });
            // 子时间轴区与主时间轴分隔线
            this.gUi.beginFill(COLOR_DARK2).drawRect(LEFT_W, subTop - 1, visW, 1).endFill();
            // 纵向滚动条（右侧）
            if (this.maxScrollY() > 0) {
                const trackX = W - RIGHT_W + 4;
                const trackW = VSCROLL_W;
                const trackH = subBottom - subTop;
                this.gUi.beginFill(0x555555).drawRect(trackX, subTop, trackW, trackH).endFill();
                const ratio = trackH / (this.subStrips.length * (SUB_H + SUB_GAP));
                const th = Math.max(20, trackH * Math.min(1, ratio));
                const ty = subTop + (this.scrollY / this.maxScrollY()) * (trackH - th);
                this.gUi.beginFill(0xbbbbbb).drawRoundedRect(trackX, ty, trackW, th, 4).endFill();
            }
        }

        // ---- 当前帧指针（红线贯穿标尺到帧区底部） ----
        const curX = this._frameX(this.currentFrameIndex) + this.frameWidth / 2;
        if (curX >= LEFT_W && curX < W - RIGHT_W + this.frameWidth) {
            this.gCursor.beginFill(COLOR_CURSOR)
                .drawRect(curX - 1, 0, 2, Math.max(0, subBottom)).endFill();
            this.gCursor.beginFill(COLOR_CURSOR)
                .drawPolygon([curX - 5, 0, curX + 5, 0, curX, 7]).endFill();
        }
        // ---- 封面帧标记（主时间轴绿色三角） ----
        const pvX = this._frameX(this.previewFrameIndex) + this.frameWidth / 2;
        if (pvX >= LEFT_W && pvX < W - RIGHT_W + this.frameWidth) {
            this.gCursor.beginFill(COLOR_PREVIEW)
                .drawPolygon([pvX - 4, mainY, pvX + 4, mainY, pvX, mainY + 6]).endFill();
        }

        // ---- 左列/右列/底部深色控制块底 ----
        this.gBkg.beginFill(COLOR_DARK).drawRect(0, 0, LEFT_W, mainY + MAIN_H).endFill();
        this.gBkg.beginFill(COLOR_DARK2).drawRect(W - RIGHT_W, 0, RIGHT_W, mainY + MAIN_H).endFill();
        // 折叠时贴在主时间轴正下方（对齐 AS3 mBottomContainer.y = mSubTimelineContainerY）
        const bottomY = this.subVisible ? H - BOTTOM_H : subTop;
        this.gBkg.beginFill(COLOR_DARK2).drawRect(0, bottomY, W, BOTTOM_H).endFill();

        // ---- 交互控件：持久化，仅脏时重建 ----
        // 播放中每帧redraw若销毁重建按钮，新建对象worldTransform未更新时
        // PIXI命中测试会打空，导致停止/折叠等按钮难点击、间歇性无效
        if (this._uiDirty || !this._uiContainer) {
            this._rebuildUi(W, mainY, bottomY);
            this._uiDirty = false;
        }

        // ---- 横向滚动条轨道（底条中段） ----
        const trackX = LEFT_W;
        const trackW = visW;
        this.gUi.beginFill(0x555555).drawRect(trackX, bottomY + 6, trackW, BOTTOM_H - 12).endFill();
        if (this.maxScrollX() > 0) {
            const ratio = this._visW(W) / this._contentW();
            const thW = Math.max(24, trackW * Math.min(1, ratio));
            const thX = trackX + (this.scrollX / this.maxScrollX()) * (trackW - thW);
            this.gUi.beginFill(0xdddddd).drawRoundedRect(thX, bottomY + 8, thW, BOTTOM_H - 16, 4).endFill();
        }
        this.thumbHit.beginFill(0, 0.0001).drawRect(trackX, bottomY, trackW, BOTTOM_H).endFill();
        this.thumbHit.hitArea = new PIXI.Rectangle(trackX, bottomY, trackW, BOTTOM_H);

        // ---- 交互热区 ----
        this.mainHit.beginFill(0, 0.0001).drawRect(LEFT_W, 0, visW, RULER_H + MAIN_H).endFill();
        this.mainHit.hitArea = new PIXI.Rectangle(LEFT_W, 0, visW, RULER_H + MAIN_H);
        if (this.subVisible) {
            this.subHit.beginFill(0, 0.0001).drawRect(LEFT_W, subTop, visW, Math.max(0, subBottom - subTop)).endFill();
            this.subHit.hitArea = new PIXI.Rectangle(0, subTop, W - RIGHT_W, Math.max(0, subBottom - subTop));
        } else {
            this.subHit.hitArea = new PIXI.Rectangle(0, 0, 0, 0);
        }

        // ---- 分隔线 ----
        this.gUi.beginFill(0x222222).drawRect(0, RULER_H - 1, W, 1).endFill();
        this.gUi.beginFill(0x222222).drawRect(LEFT_W - 1, 0, 1, subBottom).endFill();
        this.gUi.beginFill(0x222222).drawRect(W - RIGHT_W, 0, 1, subBottom).endFill();

        // 锁定遮罩
        if (this.locked) {
            this.gUi.beginFill(0xffffff, 0.4).drawRect(0, 0, W, H).endFill();
        }
    }

    // 重建交互控件层（播放/停止圆钮、折叠箭头、保存、上一帧/下一帧）。
    // 由 _uiDirty 触发：播放中每帧redraw不重建，避免新对象worldTransform未更新导致点击打空
    _rebuildUi(W, mainY, bottomY) {
        if (this._uiContainer) {
            this.stage.removeChild(this._uiContainer);
            this._uiContainer.destroy({ children: true });
        }
        this._uiContainer = new PIXI.Container();
        this.stage.addChild(this._uiContainer);

        // ---- 左列播放/停止圆钮 ----
        const playBtn = new PIXI.Graphics();
        playBtn.beginFill(0xf5f5f5).drawCircle(0, 0, 15).endFill();
        if (this.isPlaying) {
            playBtn.beginFill(COLOR_DARK).drawRect(-4, -5, 8, 10).endFill();
        } else {
            playBtn.beginFill(COLOR_DARK)
                .drawPolygon([-3, -6, -3, 6, 7, 0]).endFill();
        }
        playBtn.position.set(LEFT_W / 2 - 12, mainY + MAIN_H / 2);
        playBtn.interactive = true;
        playBtn.buttonMode = this.initData;
        playBtn.on('pointerdown', (e) => {
            e.stopPropagation();
            this.isPlaying ? this.stop() : this.play();
        });
        this._uiContainer.addChild(playBtn);

        // 左列折叠按钮（对齐 AS3 confirm_packup/confirm_unfold：左列右缘 24×52 竖条，
        // 展开时显示上箭头∧=packup，收起时显示下箭头∨=unfold）
        const foldL = new PIXI.Graphics();
        foldL.beginFill(0xf0f0f0).drawRect(0, 0, 24, MAIN_H).endFill();
        foldL.lineStyle(1, 0xcccccc).moveTo(0, 0).lineTo(0, MAIN_H);
        foldL.lineStyle(1.5, COLOR_DARK);
        const fcY = MAIN_H / 2;
        if (this.subVisible) {
            foldL.moveTo(7, fcY + 4).lineTo(12, fcY - 4).lineTo(17, fcY + 4);
        } else {
            foldL.moveTo(7, fcY - 4).lineTo(12, fcY + 4).lineTo(17, fcY - 4);
        }
        foldL.position.set(LEFT_W - 24, mainY);
        foldL.interactive = true;
        foldL.buttonMode = this.initData;
        foldL.on('pointerdown', (e) => { e.stopPropagation(); this.toggleSub(); });
        this._uiContainer.addChild(foldL);

        // ---- 右列：保存按钮（对齐 AS3 mSaveBtn confirm_*：主时间轴行高红底白勾，isedit点亮） ----
        const saveEnabled = this.initData && this.isEdit;
        const saveBtn = new PIXI.Graphics();
        const sbw = RIGHT_W - 10;
        const sbh = MAIN_H - 10;
        saveBtn.beginFill(saveEnabled ? 0xe9392a : 0x6e6e6e).drawRoundedRect(0, 0, sbw, sbh, 4).endFill();
        saveBtn.lineStyle(3, saveEnabled ? 0xffffff : 0xa8a8a8);
        saveBtn.moveTo(sbw * 0.26, sbh * 0.52).lineTo(sbw * 0.45, sbh * 0.72).lineTo(sbw * 0.78, sbh * 0.28);
        saveBtn.position.set(W - RIGHT_W + 5, mainY + 5);
        saveBtn.interactive = true;
        saveBtn.buttonMode = saveEnabled;
        if (saveEnabled) saveBtn.on('pointerdown', (e) => { e.stopPropagation(); this.sendSave(); });
        this._uiContainer.addChild(saveBtn);

        // ---- 底部：上一帧/下一帧 ----
        const mkSq = (x, label, cb, enabled) => {
            const g = new PIXI.Graphics();
            g.beginFill(enabled === false ? 0x888888 : 0xf5f5f5).drawRect(0, 0, 18, 16).endFill();
            const t = new PIXI.Text(label, { fontSize: 11, fill: enabled === false ? 0xaaaaaa : 0x333333 });
            t.anchor.set(0.5);
            t.position.set(9, 8);
            const c = new PIXI.Container();
            c.addChild(g, t);
            c.position.set(x, bottomY + 4);
            c.interactive = true;
            c.buttonMode = enabled !== false;
            if (enabled !== false) c.on('pointerdown', (e) => { e.stopPropagation(); cb(); });
            this._uiContainer.addChild(c);
        };
        mkSq(6, '◀', () => this.prevFrame(), this.initData);
        mkSq(30, '▶', () => this.nextFrame(), this.initData);
    }

    _drawStrip(strip, y, h, isMain, W) {
        const visW = this._visW(W);
        const firstIdx = Math.max(0, Math.floor(this.scrollX / this.frameWidth));
        const lastIdx = Math.min(this._gridFrameNum() - 1,
            Math.ceil((this.scrollX + visW) / this.frameWidth));

        const colorOf = (type) => {
            switch (type) {
                case FRAME_KEY: return COLOR_KEY;
                case FRAME_TWEEN: return COLOR_TWEEN;
                case FRAME_NORMAL: return COLOR_NORMAL;
                default: return COLOR_EMPTY;
            }
        };

        for (let i = firstIdx; i <= lastIdx; i++) {
            const x = this._frameX(i);
            const type = strip.frames[i] || FRAME_EMPTY;
            const selected = strip.selectMin >= 0 && i >= strip.selectMin && i <= strip.selectMax;
            // 帧分隔线：AS3每个9px帧槽=左缘1px深线(0x3d3d3d)+8px帧格，全行连续灰带
            this.gStrips.beginFill(COLOR_FRAME_LINE)
                .drawRect(x, y, 1, h).endFill();
            // 帧格
            this.gStrips.beginFill(selected ? COLOR_SELECTED : colorOf(type))
                .drawRect(x + 1, y, this.frameWidth - 1, h).endFill();
            // 关键帧白点
            if (type === FRAME_KEY) {
                this.gStrips.beginFill(0xffffff)
                    .drawCircle(x + this.frameWidth / 2, y + h / 2,
                        Math.min(3, this.frameWidth / 3))
                    .endFill();
            }
            // 24帧分隔线加粗（AS3 line纹理，视觉上比单帧分隔线粗）
            if (i > 0 && i % 24 === 0) {
                this.gStrips.beginFill(COLOR_LINE24)
                    .drawRect(x, y, 2, h).endFill();
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/common/scripts/pixi/ActionEditorTimeline.js